import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    TextField,
    Typography,
} from '@mui/material';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import { debounce } from 'lodash';

interface PlaceType {
    description: string;
    place_id: string;
    // Structured halves of the prediction (street vs. locality) when the
    // Places API provides them — the dropdown renders them as two lines.
    main_text?: string;
    secondary_text?: string;
}

// Split a full description into street / locality halves for predictions
// that arrive without structured formatting.
const splitDescription = (description: string): { main: string; secondary: string } => {
    const commaAt = description.indexOf(',');
    if (commaAt === -1) return { main: description, secondary: '' };
    return { main: description.slice(0, commaAt), secondary: description.slice(commaAt + 1).trim() };
};

interface Props {
    onAddressSelect?: (address: string, placeId: string) => void;
    initialValue?: PlaceType;
    // When set, render a plain placeholder instead of MUI's floating label —
    // hosts that draw their own field label (the IRA search bar) need the
    // input text on the same baseline as sibling placeholder inputs.
    placeholder?: string;
}

const NEARBY_SEARCH_RADIUS_METERS = 100;

export const GooglePlacesAutocomplete: React.FC<Props> = ({
                                                              onAddressSelect,
                                                              initialValue,
                                                              placeholder,
                                                          }) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedValue, setSelectedValue] = useState<PlaceType | null>(initialValue || null);
    const [options, setOptions] = useState<PlaceType[]>([]);
    const [apiReady, setApiReady] = useState(false);

    // Wait for Google Maps API to be fully loaded
    useEffect(() => {
        const checkApiReady = () => {
            if (window.google?.maps?.places) {
                setApiReady(true);
                return true;
            }
            return false;
        };

        if (checkApiReady()) return;

        const interval = setInterval(() => {
            if (checkApiReady()) {
                clearInterval(interval);
            }
        }, 100);

        const timeout = setTimeout(() => {
            clearInterval(interval);
            console.warn('Google Maps API failed to load within 10 seconds');
        }, 10000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, []);

    // Set initial value for controlled input and selected item
    useEffect(() => {
        setInputValue(initialValue?.description || '');
        setSelectedValue(initialValue || null);
    }, [initialValue]);

    // Debounced fetch for predictions with improved error handling
    const fetchPlacePredictions = debounce(
        async (input: string) => {
            if (!input || !apiReady || !window.google?.maps?.places) return;

            try {
                // Try the new AutocompleteSuggestion API first if available
                try {
                    if (window.google.maps.places.AutocompleteSuggestion) {
                        const request = {
                            input,
                            includedRegionCodes: ['us'],
                        };

                        const response = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

                        if (response?.suggestions) {
                            const places = response.suggestions.map((suggestion: any) => ({
                                description: suggestion.placePrediction?.text?.text || suggestion.text?.text || '',
                                place_id: suggestion.placePrediction?.placeId || suggestion.placeId || '',
                                main_text: suggestion.placePrediction?.structuredFormat?.mainText?.text,
                                secondary_text: suggestion.placePrediction?.structuredFormat?.secondaryText?.text,
                            }));
                            setOptions(places);
                            return;
                        }
                    }
                } catch (newApiError) {
                    console.debug('New AutocompleteSuggestion API not available, using fallback');
                }

                // Fallback to old API
                if (window.google.maps.places.AutocompleteService) {
                    const service = new google.maps.places.AutocompleteService();
                    service.getPlacePredictions(
                        {
                            input,
                            types: ['address'],
                            componentRestrictions: { country: 'US' },
                        },
                        (predictions, status) => {
                            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
                                const places = predictions.map((prediction) => ({
                                    description: prediction.description,
                                    place_id: prediction.place_id,
                                    main_text: prediction.structured_formatting?.main_text,
                                    secondary_text: prediction.structured_formatting?.secondary_text,
                                }));
                                setOptions(places);
                            } else {
                                setOptions([]);
                            }
                        }
                    );
                }
            } catch (error) {
                console.warn('Error fetching autocomplete suggestions:', error);
                setOptions([]);
            }
        },
        300
    );

    // Fetch when input changes and API is ready
    useEffect(() => {
        if (inputValue && apiReady) {
            fetchPlacePredictions(inputValue);
        }
    }, [inputValue, apiReady]);

    // Extract street number from address
    const extractStreetNumber = (address: string): string => {
        const match = address.match(/^\d+[\w-]*/);
        return match ? match[0] : '';
    };

    // Find valid addresses with street numbers within radius using reverse geocoding
    const findNearbyValidAddresses = async (
        latitude: number,
        longitude: number,
        radiusMeters: number = 100
    ): Promise<Array<{ place_id: string; formattedAddress: string; distance: number }>> => {
        if (!window.google?.maps) return [];

        try {
            console.log(`🔍 Searching for valid addresses within ${radiusMeters}m of (${latitude}, ${longitude})`);

            const geocoder = new google.maps.Geocoder();
            const centerLocation = new google.maps.LatLng(latitude, longitude);

            // Reverse geocode to get all addresses at this location
            return new Promise((resolve) => {
                geocoder.geocode(
                    { location: centerLocation },
                    (results, status) => {
                        if (status === 'OK' && results) {
                            console.log(`Found ${results.length} results from reverse geocoding`);

                            const validAddresses: Array<{ place_id: string; formattedAddress: string; distance: number }> = [];

                            results.forEach((result, index) => {
                                const address = result.formatted_address || '';
                                const streetNumber = extractStreetNumber(address);

                                // Check if it has street number and street name components
                                const hasStreetNumber = result.address_components?.some(
                                    component => component.types.includes('street_number')
                                );

                                const hasStreetName = result.address_components?.some(
                                    component => component.types.includes('route')
                                );

                                const resultLocation = result.geometry?.location;
                                let distance = 0;

                                if (resultLocation) {
                                    // Calculate distance from center
                                    distance = google.maps.geometry.spherical.computeDistanceBetween(
                                        centerLocation,
                                        resultLocation
                                    );
                                }

                                console.log(`  ${index + 1}. ${address}`, {
                                    streetNumber,
                                    hasStreetNumber,
                                    hasStreetName,
                                    distance: `${distance.toFixed(1)}m`,
                                    withinRadius: distance <= radiusMeters
                                });

                                if (hasStreetNumber && hasStreetName && streetNumber && distance <= radiusMeters) {
                                    validAddresses.push({
                                        place_id: result.place_id || '',
                                        formattedAddress: address,
                                        distance: distance
                                    });
                                }
                            });

                            // Sort by distance (closest first)
                            validAddresses.sort((a, b) => a.distance - b.distance);

                            console.log(`✅ Found ${validAddresses.length} valid addresses within ${radiusMeters}m`);
                            resolve(validAddresses);
                        } else {
                            console.warn('Reverse geocoding failed:', status);
                            resolve([]);
                        }
                    }
                );
            });
        } catch (error) {
            console.warn('Error finding nearby addresses:', error);
            return [];
        }
    };

    // Geocode and find nearest valid address if needed
    const geocodeAndFindValidAddress = async (
        address: string
    ): Promise<{ place_id: string; formattedAddress: string; isApproximate: boolean }> => {
        if (!window.google?.maps) return { place_id: '', formattedAddress: address, isApproximate: false };

        try {
            const geocoder = new google.maps.Geocoder();

            return new Promise(async (resolve) => {
                geocoder.geocode(
                    {
                        address: address,
                        componentRestrictions: { country: 'US' }
                    },
                    async (results, status) => {
                        if (status === 'OK' && results && results.length > 0) {
                            const result = results[0];
                            const formattedAddress = result.formatted_address || '';

                            // Check if result has street number
                            const hasStreetNumber = result.address_components?.some(
                                component => component.types.includes('street_number')
                            );

                            if (hasStreetNumber) {
                                // Valid address found directly
                                console.log('✅ Direct geocoding successful with street number:', formattedAddress);
                                resolve({
                                    place_id: result.place_id || '',
                                    formattedAddress: formattedAddress,
                                    isApproximate: false
                                });
                            } else {
                                // No street number - search within 100m for valid addresses
                                console.log('⚠️ Geocoded location has no street number, searching within 100m...');

                                const location = result.geometry.location;
                                const nearbyAddresses = await findNearbyValidAddresses(
                                    location.lat(),
                                    location.lng(),
                                    NEARBY_SEARCH_RADIUS_METERS
                                );

                                if (nearbyAddresses.length > 0) {
                                    const closest = nearbyAddresses[0];
                                    console.log(`✅ Found valid address ${closest.distance.toFixed(1)}m away:`, closest.formattedAddress);
                                    resolve({
                                        place_id: closest.place_id,
                                        formattedAddress: closest.formattedAddress,
                                        isApproximate: true
                                    });
                                } else {
                                    console.warn('❌ No valid addresses found within 100m');
                                    resolve({
                                        place_id: '',
                                        formattedAddress: address,
                                        isApproximate: false
                                    });
                                }
                            }
                        } else {
                            console.warn('Geocoding failed:', status);
                            resolve({
                                place_id: '',
                                formattedAddress: address,
                                isApproximate: false
                            });
                        }
                    }
                );
            });
        } catch (error) {
            console.warn('Error in geocoding:', error);
            return { place_id: '', formattedAddress: address, isApproximate: false };
        }
    };

    // Handle blur to capture custom input
    const handleBlur = async () => {
        const inputMatchesSelected = selectedValue && inputValue === selectedValue.description;

        if (inputValue && !inputMatchesSelected) {
            // Step 1: Single option in dropdown
            if (options.length === 1) {
                setSelectedValue(options[0]);
                if (onAddressSelect) {
                    onAddressSelect(options[0].description, options[0].place_id);
                }
                return;
            }

            // Step 2: Exact match
            const exactMatch = options.find(
                option => option.description.toLowerCase() === inputValue.toLowerCase()
            );

            if (exactMatch) {
                setSelectedValue(exactMatch);
                if (onAddressSelect) {
                    onAddressSelect(exactMatch.description, exactMatch.place_id);
                }
                return;
            }

            // Step 3: Geocode and find valid address
            console.log('No match in dropdown, geocoding:', inputValue);
            const result = await geocodeAndFindValidAddress(inputValue);

            const finalPlace: PlaceType = {
                description: inputValue,
                place_id: result.place_id,
            };

            setSelectedValue(finalPlace);
            if (onAddressSelect) {
                onAddressSelect(inputValue, result.place_id);
            }

            // Log if address was approximated or unverified
            if (result.isApproximate) {
                console.info('ℹ️ Address approximated to nearest valid address within 100m');
            }

            if (!result.place_id) {
                console.warn('📋 UNVERIFIED ADDRESS - Please review:', {
                    userInput: inputValue,
                    timestamp: new Date().toISOString()
                });
            }
        } else if (!inputValue && selectedValue) {
            setSelectedValue(null);
            if (onAddressSelect) {
                onAddressSelect('', '');
            }
        }
    };

    return (
        <Autocomplete
            value={selectedValue}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
            }}
            onChange={(_, newValue) => {
                if (newValue) {
                    if (typeof newValue === 'string') {
                        const customPlace: PlaceType = {
                            description: newValue,
                            place_id: '',
                        };
                        setSelectedValue(customPlace);
                        if (onAddressSelect) {
                            onAddressSelect(newValue, '');
                        }
                    } else {
                        setSelectedValue(newValue);
                        if (onAddressSelect) {
                            onAddressSelect(newValue.description, newValue.place_id);
                        }
                    }
                } else {
                    setSelectedValue(null);
                    if (onAddressSelect) {
                        onAddressSelect('', '');
                    }
                }
            }}
            options={options}
            getOptionLabel={(option) => {
                if (typeof option === 'string') {
                    return option;
                }
                return option.description || '';
            }}
            filterOptions={(x) => x}
            freeSolo
            noOptionsText={!apiReady ? 'Loading Google Maps...' : (inputValue.length > 0 ? 'No address found' : 'Start typing to search addresses')}
            autoComplete
            fullWidth
            disabled={!apiReady}
            slotProps={{
                paper: {
                    sx: {
                        mt: 1,
                        borderRadius: '16px',
                        border: '1px solid #E9EEF4',
                        boxShadow: '0 16px 40px rgba(16, 42, 67, 0.16)',
                        overflow: 'hidden',
                        '& .MuiAutocomplete-listbox': {
                            py: 0.75,
                            px: 0.75,
                            '& .MuiAutocomplete-option': {
                                borderRadius: '10px',
                                px: 1.25,
                                py: 1,
                                gap: 1.25,
                                alignItems: 'center',
                                '&.Mui-focused': { backgroundColor: '#F3F7FB' },
                                '&[aria-selected="true"], &[aria-selected="true"].Mui-focused': { backgroundColor: '#E8F6FD' },
                            },
                        },
                        '& .MuiAutocomplete-noOptions': { fontSize: 14, color: '#6B7686', px: 2, py: 1.5 },
                    },
                },
            }}
            renderOption={(props, option) => {
                const place = option as PlaceType;
                const fallback = splitDescription(place.description || '');
                const main = place.main_text || fallback.main;
                const secondary = place.secondary_text ?? fallback.secondary;
                return (
                    <li {...props} key={place.place_id || place.description}>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                backgroundColor: '#EAF6FC',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <LocationOnOutlined sx={{ fontSize: 17, color: '#11A7E2' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: '#0E1230', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {main}
                            </Typography>
                            {secondary && (
                                <Typography sx={{ fontSize: 12.5, color: '#6B7686', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {secondary}
                                </Typography>
                            )}
                        </Box>
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...(placeholder
                        ? { placeholder: apiReady ? placeholder : 'Loading Google Maps...' }
                        : { label: apiReady ? 'US Street Address' : 'Loading Google Maps...' })}
                    variant="outlined"
                    fullWidth
                    disabled={!apiReady}
                    onBlur={handleBlur}
                />
            )}
        />
    );
};